import { USE_MOCKED_USER } from 'config/app';
import { SigninRedirectArgs, User, UserManager } from 'oidc-client-ts';

import { localSettings } from './config';
/**
 * 1. The Relying Parties (RP) (Client) sends and request to the OpenID Provider (OP)
 * 2. OP authenticates the end user and obtains authorization (redirect page)
 * 3. OP responds with an ID Token (JWT) and ususally an Access Token (Bearer?)
 * 4. RP can send rquest with the access token to the userinfo endpoint
 * 5. User Info endpoint returns claims about the end-user
 */
const fakeUser: Pick<User, 'access_token'> = {
  access_token: 'Mocke Karlsson',
};

const userManager = new UserManager(localSettings);

// Retry budget for IdP calls. 20s per attempt is generous — a cold IdP
// often needs 10s+ to wake. Backoffs are applied between attempts.
const OIDC_TIMEOUT_MS = 20000;
const OIDC_ATTEMPTS = 3;
const OIDC_BACKOFFS_MS = [1000, 2000];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

// Bounded timeout + retry for oidc-client-ts calls. The library has no
// top-level timeout (silentRequestTimeoutInSeconds only affects iframe
// silent renew), so without this an unresponsive IdP would hang the saga
// forever and the loading slot would never clear.
//
// Note for signinRedirect: only guards issuing the redirect, not the IdP's
// response — that comes back as a separate roundtrip to /signin-oidc.
async function withTimeoutAndRetry<T>(
  label: string,
  fn: () => Promise<T>,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < OIDC_ATTEMPTS; attempt++) {
    try {
      // Race the call against a timer. Late resolves from a timed-out
      // attempt are ignored — we only consume the winner.
      return await new Promise<T>((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error(`OIDC "${label}" timed out`)),
          OIDC_TIMEOUT_MS,
        );
        fn().then(
          (value) => {
            clearTimeout(timer);
            resolve(value);
          },
          (error) => {
            clearTimeout(timer);
            reject(error);
          },
        );
      });
    } catch (error) {
      lastError = error;
      if (attempt >= OIDC_ATTEMPTS - 1) break;
      const backoff = OIDC_BACKOFFS_MS[attempt] ?? 0;
      // eslint-disable-next-line no-console
      console.warn(`OIDC "${label}" attempt ${attempt + 1} failed`, error);
      await sleep(backoff);
    }
  }

  throw lastError;
}

// Get authenticated user
const getUser = () => {
  if (USE_MOCKED_USER) return Promise.resolve(fakeUser);
  return withTimeoutAndRetry('getUser', () => userManager.getUser());
};

// Redirect to signin page. `args.state` is round-tripped through the IdP and
// returned via signinRedirectCallback().state — used to carry the post-login
// target URL so the user lands where they originally requested.
const signinRedirect = (args?: SigninRedirectArgs) => {
  if (USE_MOCKED_USER) return Promise.resolve();
  return withTimeoutAndRetry('signinRedirect', () =>
    userManager.signinRedirect(args),
  );
};

// When redirected after succes
const signinRedirectCallback = () => {
  if (USE_MOCKED_USER) return Promise.resolve(fakeUser);
  return withTimeoutAndRetry('signinRedirectCallback', () =>
    userManager.signinRedirectCallback(),
  );
};

const signoutRedirect = () => {
  if (USE_MOCKED_USER) return Promise.resolve();
  return withTimeoutAndRetry('signoutRedirect', () =>
    userManager.signoutRedirect(),
  );
};

export { getUser, signinRedirect, signinRedirectCallback, signoutRedirect };
